import React, { useEffect, useMemo, useRef } from 'react';
import { Layer, Transformer } from 'react-konva';

import { isLineShape } from '../config';
import { ELEMENT_TYPES, getElementNodeId } from '../core/documentSchema';
import { useMediaStudio } from '../context/MediaStudioContext';
import { ANCHOR_SCREEN_PX, getScreenSizedAnchorStyle } from '../utils/screenSizedAnchor';

const ROTATION_SNAPS = [0, 90, 180, 270];
const ROTATION_SNAP_TOLERANCE = 3;

const isAxisAligned = (rotation) => {
  const normalized = ((rotation % 360) + 360) % 360;
  return normalized < 0.01 || normalized > 359.99;
};

function SelectionTransformer({
  stageRef,
  selectedId,
  selectedIds = [],
  selectedElement,
  elements,
  zoom,
  enabled,
  onSnapResize,
}) {
  const transformerRef = useRef(null);
  const { themeColors, layoutMode } = useMediaStudio();
  const anchorStyle = useMemo(
    () => getScreenSizedAnchorStyle(layoutMode.isCoarsePointer),
    [layoutMode.isCoarsePointer],
  );
  const widthOnly = selectedElement?.type === ELEMENT_TYPES.TEXT || isLineShape(selectedElement);
  const isMulti = selectedIds.length > 1;

  useEffect(() => {
    if (!enabled || !transformerRef.current || !stageRef.current) {
      transformerRef.current?.nodes([]);
      return;
    }

    const stage = stageRef.current;
    if (isMulti) {
      const nodes = elements
        .filter((element) => selectedIds.includes(element.id) && element.visible && !element.locked)
        .map((element) => stage.findOne(`#${getElementNodeId(element.id)}`))
        .filter(Boolean);
      transformerRef.current.nodes(nodes);
      transformerRef.current.keepRatio(false);
      transformerRef.current.getLayer()?.batchDraw();
      return;
    }
    const selectedNode = stage.findOne(`#${getElementNodeId(selectedId)}`);
    if (selectedNode && selectedElement?.visible && !selectedElement.locked) {
      transformerRef.current.nodes([selectedNode]);
      transformerRef.current.keepRatio(Boolean(selectedElement.keepAspectRatio));
      transformerRef.current.getLayer()?.batchDraw();
    } else {
      transformerRef.current.nodes([]);
    }
  }, [enabled, isMulti, selectedId, selectedIds, selectedElement, elements, stageRef, zoom]);

  const handleAnchorDragBound = (oldPos, newPos) => {
    const transformer = transformerRef.current;
    const node = transformer?.nodes()[0];
    const anchor = transformer?.getActiveAnchor() || '';
    const snapX = anchor.includes('left') || anchor.includes('right');
    const snapY = anchor.includes('top') || anchor.includes('bottom');

    if (!onSnapResize || !node || !isAxisAligned(node.rotation()) || (!snapX && !snapY)) {
      return newPos;
    }

    const stageTransform = stageRef.current.getAbsoluteTransform();
    const point = stageTransform.copy().invert().point(newPos);
    const snapped = onSnapResize({ x: point.x, y: point.y, snapX, snapY });
    return stageTransform.point(snapped);
  };

  if (!enabled) {
    return null;
  }

  return (
    <Layer>
      <Transformer
        ref={transformerRef}
        rotateEnabled={!isMulti}
        resizeEnabled={!isMulti}
        rotationSnaps={ROTATION_SNAPS}
        rotationSnapTolerance={ROTATION_SNAP_TOLERANCE}
        anchorSize={ANCHOR_SCREEN_PX}
        anchorStyleFunc={anchorStyle}
        borderStroke={themeColors.accent}
        anchorStroke={themeColors.accent}
        anchorFill={themeColors.accentContrast}
        keepRatio={Boolean(selectedElement?.keepAspectRatio)}
        anchorDragBoundFunc={handleAnchorDragBound}
        enabledAnchors={
          widthOnly
            ? ['middle-left', 'middle-right']
            : selectedElement?.keepAspectRatio
            ? ['top-left', 'top-right', 'bottom-left', 'bottom-right']
            : [
                'top-left',
                'top-center',
                'top-right',
                'middle-right',
                'middle-left',
                'bottom-left',
                'bottom-center',
                'bottom-right',
              ]
        }
        boundBoxFunc={(oldBox, newBox) => {
          if (Math.abs(newBox.rotation - oldBox.rotation) > 1e-6) {
            return newBox;
          }
          if (newBox.width < 10 || (!widthOnly && newBox.height < 10)) {
            return oldBox;
          }
          if (widthOnly) {
            return { ...newBox, height: oldBox.height, y: oldBox.y };
          }
          return newBox;
        }}
      />
    </Layer>
  );
}

export default SelectionTransformer;
